import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Film, Users, Tag, ChevronRight } from "lucide-react";
import axios from "axios";
import VideoEmbed from "../components/VideoEmbed";
import { PageSEO, ProjectSchema, BreadcrumbSchema } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getStatusClass(status) {
  if (status === "Released") return "status-released";
  if (status === "In Production") return "status-production";
  return "status-development";
}

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(`${API}/projects/${slug}`);
        setProject(res.data);
      } catch (e) {
        console.error("Failed to fetch project", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="w-6 h-6 border border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#050505]">
        <PageSEO title="Project Not Found" description="The requested project could not be found." path={`/projects/${slug}`} />
        <p className="text-[#52525B] text-sm">Project not found</p>
        <Link to="/projects" className="text-xs text-[#38BDF8] hover:underline uppercase tracking-wider">Back to Projects</Link>
      </div>
    );
  }

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Projects", path: "/projects" },
    { name: project.title, path: `/projects/${project.slug}` },
  ];

  const combinedSchema = [
    ProjectSchema(project),
    BreadcrumbSchema(breadcrumbs),
  ];

  return (
    <div data-testid="project-detail-page" className="min-h-screen bg-[#050505]">
      <PageSEO
        title={`${project.title} — ${project.genre} ${project.status === "Released" ? "Film" : "Project"}`}
        description={project.synopsis || `${project.title} is a ${project.genre} project by Mist Media. ${project.status}. ${project.cast_crew}`}
        path={`/projects/${project.slug}`}
        image={project.poster_image}
        type="video.movie"
        schema={combinedSchema}
      />

      {/* Hero Image */}
      <div className="relative h-[55vh] md:h-[70vh] overflow-hidden">
        <img
          src={project.poster_image}
          alt={`${project.title} - ${project.genre} ${project.status === "Released" ? "film" : "project"} poster by Mist Media`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#050505]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/40 via-transparent to-[#050505]/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 -mt-40 px-6 md:px-12 lg:px-24 pb-28">
        <div className="max-w-[900px] mx-auto">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <ol className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">
                <li><Link to="/" className="text-[#3f3f46] hover:text-white transition-colors">Home</Link></li>
                <li><ChevronRight size={10} className="text-[#27272A]" /></li>
                <li><Link to="/projects" className="text-[#3f3f46] hover:text-white transition-colors">Projects</Link></li>
                <li><ChevronRight size={10} className="text-[#27272A]" /></li>
                <li className="text-[#71717A]">{project.title}</li>
              </ol>
            </nav>

            {/* Status & Genre */}
            <div className="flex items-center gap-3 mb-5">
              <span className={`text-[9px] uppercase tracking-[0.2em] font-bold px-3 py-1 border rounded-none ${getStatusClass(project.status)}`}>
                {project.status}
              </span>
              <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#52525B]">
                {project.genre}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-['Syne'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-[-0.04em] text-white mb-10 leading-[0.9]">
              {project.title}
            </h1>

            {/* Meta strip */}
            <div className="flex flex-wrap gap-8 mb-12 pb-12 border-b border-[#1A1A1A]">
              {project.release_date && (
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-1">Year</p>
                  <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Calendar size={13} />
                    <span>{project.release_date}</span>
                  </div>
                </div>
              )}
              {project.genre && (
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-1">Genre</p>
                  <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Tag size={13} />
                    <span>{project.genre}</span>
                  </div>
                </div>
              )}
              {project.status && (
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-1">Status</p>
                  <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Film size={13} />
                    <span>{project.status}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Synopsis */}
            {project.synopsis && (
              <section className="mb-14">
                <h2 className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8] mb-5">Synopsis</h2>
                <p className="text-base md:text-lg text-[#A1A1AA] leading-[1.8] font-light">
                  {project.synopsis}
                </p>
              </section>
            )}

            {/* Video */}
            {project.teaser_video && (
              <section className="mb-14">
                <h2 className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8] mb-5">Teaser</h2>
                <VideoEmbed url={project.teaser_video} />
              </section>
            )}

            {/* Cast & Crew */}
            {project.cast_crew && (
              <section className="mb-14">
                <h2 className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8] mb-5">
                  <Users size={12} className="inline mr-2" />Cast & Crew
                </h2>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{project.cast_crew}</p>
              </section>
            )}

            {/* Back link */}
            <Link
              to="/projects"
              data-testid="back-to-projects"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#52525B] hover:text-white transition-colors"
            >
              <ArrowLeft size={12} /> Back to All Projects
            </Link>
          </motion.article>
        </div>
      </div>
    </div>
  );
}
